import { loadBaseCSS } from './button-utils.js';
import { attachInternalsSafe, generateControlId, isAttrTruthy, parseSize, parseValidateOn, reflectBoolean, upgradeProperty } from './form-control.js';
import { primitiveStyles } from './primitive-utils.js';
import { normalizeChoiceOptions } from './option-model.js';
import { redirectValidationFocus } from './focus-utils.js';
import type { QdsOption } from '../../types/index';

export interface ChoiceValidity { valid: boolean; valueMissing: boolean; customError: boolean; }

/**
 * Native form/value lifecycle shared by small, inline choice controls.
 * Native invalid reporting uses an isolated input and returns focus to the public
 * control after the browser focus cycle, preserving labels and invalid cancellation.
 */
export abstract class QdsChoiceBase extends HTMLElement {
  static formAssociated = true;
  static qdsFieldConsumer = true;
  static get observedAttributes(): string[] {
    return ['value', 'multiple', 'name', 'form', 'label', 'aria-label', 'aria-labelledby', 'hint', 'error-text', 'disabled', 'readonly', 'required', 'invalid', 'size', 'validate-on'];
  }
  protected _control?: HTMLDivElement;
  protected _internals?: ElementInternals;
  private _validationAnchor?: HTMLInputElement;
  private _onValidationFocus = redirectValidationFocus.bind(this);
  protected _options: QdsOption[] = [];
  protected _id = generateControlId('choice');
  private _requested: string[] = [];
  private _defaults?: string[];
  private _dirty = false;
  private _optionsChanged = true;
  private _fieldsetDisabled = false;
  private _showError = false;
  private _customError = '';
  private _validity: ChoiceValidity = { valid: true, valueMissing: false, customError: false };
  private _message = '';
  constructor() {
    super(); this.attachShadow({ mode: 'open' }); loadBaseCSS();
    const internals = attachInternalsSafe(this);
    if (typeof internals?.setFormValue === 'function' && typeof internals?.setValidity === 'function') this._internals = internals;
  }
  get options(): QdsOption[] { return this._options.map(option => ({ ...option })); }
  set options(value: readonly QdsOption[]) {
    this._options = normalizeChoiceOptions(value);
    this._optionsChanged = true; this._sync();
  }
  get values(): string[] {
    const selected = new Set(this._requested);
    const values = this._options.filter(option => !option.disabled && selected.has(option.value)).map(option => option.value);
    return this.multiple ? values : values.slice(0, 1);
  }
  set values(values: readonly string[]) { this._setValues(values); }
  get value(): string { return this.values[0] ?? ''; }
  set value(value: string) { this._setValues(typeof value === 'string' && value ? [value] : []); }
  get defaultValues(): string[] { return this._defaults ? [...this._defaults] : this.getAttribute('value') ? [this.getAttribute('value')!] : []; }
  set defaultValues(value: readonly string[]) { this._defaults = this._normalize(value); if (!this._dirty) this._requested = [...this._defaults]; this._sync(); }
  get multiple(): boolean { return isAttrTruthy(this.getAttribute('multiple')); }
  set multiple(value: boolean) { reflectBoolean(this, 'multiple', value); }
  get name(): string { return this.getAttribute('name') || ''; }
  set name(value: string) { this.setAttribute('name', value ?? ''); }
  get label(): string { return this.getAttribute('label') || ''; }
  set label(value: string) { this.setAttribute('label', value ?? ''); }
  get hint(): string { return this.getAttribute('hint') || ''; }
  set hint(value: string) { this.setAttribute('hint', value ?? ''); }
  get errorText(): string { return this.getAttribute('error-text') || ''; }
  set errorText(value: string) { this.setAttribute('error-text', value ?? ''); }
  get disabled(): boolean { return this._fieldsetDisabled || isAttrTruthy(this.getAttribute('disabled')); }
  set disabled(value: boolean) { reflectBoolean(this, 'disabled', value); }
  get readonly(): boolean { return isAttrTruthy(this.getAttribute('readonly')); }
  set readonly(value: boolean) { reflectBoolean(this, 'readonly', value); }
  get required(): boolean { return isAttrTruthy(this.getAttribute('required')); }
  set required(value: boolean) { reflectBoolean(this, 'required', value); }
  get invalid(): boolean { return isAttrTruthy(this.getAttribute('invalid')); }
  set invalid(value: boolean) { reflectBoolean(this, 'invalid', value); }
  get size(): 'sm' | 'md' | 'lg' { return parseSize(this.getAttribute('size')); }
  set size(value: string) { this.setAttribute('size', value); }
  get validateOn(): 'blur' | 'input' | 'submit' { return parseValidateOn(this.getAttribute('validate-on')); }
  set validateOn(value: string) { this.setAttribute('validate-on', value); }
  get form(): HTMLFormElement | null {
    if (this._internals) return this._internals.form;
    const target = this.hasAttribute('form') ? this.ownerDocument.getElementById(this.getAttribute('form')!) : this.closest('form');
    return target?.tagName === 'FORM' ? target as HTMLFormElement : null;
  }
  get validity(): ChoiceValidity { return { ...this._validity }; }
  get validationMessage(): string { return this._message; }
  get willValidate(): boolean { return !this.disabled && !this.readonly; }
  setCustomValidity(message: string): void { this._customError = String(message ?? ''); this._sync(); }
  checkValidity(): boolean {
    this._validate();
    if (this._internals) return this._internals.checkValidity();
    if (!this.validity.valid) this.dispatchEvent(new Event('invalid', { cancelable: true }));
    return this.validity.valid;
  }
  reportValidity(): boolean {
    this._showError = true; this._sync();
    if (this._internals) return this._internals.reportValidity();
    const valid = this.checkValidity(); if (!valid) this.focus(); return valid;
  }
  protected abstract get _choiceStyles(): string;
  protected abstract _syncChoices(): void;
  protected abstract _bind(): void;
  protected abstract _unbind(): void;
  protected _buttons(): HTMLButtonElement[] { return [...this._control!.querySelectorAll<HTMLButtonElement>('[data-choice]')]; }
  protected get _focusTarget(): HTMLElement | undefined { return this._buttons().find(button => !button.disabled && button.tabIndex === 0) || this._buttons().find(button => !button.disabled) || this._control; }
  override focus(options?: FocusOptions): void { if (this._control) this._focusTarget?.focus(options); }
  connectedCallback(): void {
    for (const name of ['options', 'multiple', 'defaultValues', 'value', 'values', 'name', 'label', 'hint', 'errorText', 'disabled', 'readonly', 'required', 'invalid', 'size', 'validateOn']) upgradeProperty(this, name);
    if (!this._dirty) this._requested = this.defaultValues;
    if (!this._control) {
      this.shadowRoot!.innerHTML = `<style>${primitiveStyles}
        :host { display: inline-block; max-width: 100%; position: relative; }
        .validation-anchor { position:absolute; inset-block-start:0; inset-inline-start:0; width:1px; height:1px; padding:0; border:0; opacity:0; pointer-events:none; }
        .label-text { display: block; font-weight: 500; margin-block-end: .25rem; }
        .feedback { display: grid; min-block-size: 1.4em; font-size: .875em; margin-block-start: .25rem; }
        .feedback > * { grid-area: 1 / 1; }
        .feedback > [hidden] { display: block !important; visibility: hidden; }
        [part=error] { color: var(--theme-text-primary, #333); border-inline-start: 3px solid var(--theme-error, #8b1f00); padding-inline-start: .5rem; }
        [part=hint] { color: var(--theme-text-secondary, #555); }
        [part=options]:focus { outline: 2px solid var(--theme-accent-primary, #445e89); outline-offset: 2px; }
        ${this._choiceStyles}
      </style><span class="label-text" part="label" id="${this._id}-label"></span><div part="options" tabindex="-1"></div><div class="feedback"><span part="hint" id="${this._id}-hint"></span><span part="error" id="${this._id}-error" aria-live="polite"></span></div><input class="validation-anchor" type="button" tabindex="-1" aria-hidden="true">`;
      this._control = this.shadowRoot!.querySelector('[part=options]')!;
      this._validationAnchor = this.shadowRoot!.querySelector('.validation-anchor')!;
    }
    this._validationAnchor!.addEventListener('focus', this._onValidationFocus);
    this._control.addEventListener('click', this._onClick); this._control.addEventListener('focusout', this._onFocusOut);
    this.addEventListener('invalid', this._onInvalid); this._bind(); this._sync();
  }
  disconnectedCallback(): void {
    this._validationAnchor?.removeEventListener('focus', this._onValidationFocus);
    this._control?.removeEventListener('click', this._onClick); this._control?.removeEventListener('focusout', this._onFocusOut);
    this.removeEventListener('invalid', this._onInvalid); this._unbind();
  }
  attributeChangedCallback(name: string): void {
    if (!this._dirty) this._requested = this.defaultValues;
    if (name === 'multiple' && !this.multiple) this._requested = [this.value || this._requested[0]].filter(Boolean);
    this._sync();
  }
  formDisabledCallback(disabled: boolean): void { this._fieldsetDisabled = disabled; this._sync(); }
  formResetCallback(): void { this._dirty = false; this._showError = false; this._requested = this.defaultValues; this._sync(); }
  formStateRestoreCallback(state: string | File | FormData): void {
    if (typeof state !== 'string') return;
    try { const values: unknown = JSON.parse(state); if (!Array.isArray(values)) return; this.values = values; } catch { /* Ignore an incompatible stored state. */ }
  }
  private _normalize(values: readonly string[]): string[] { return [...new Set((Array.isArray(values) ? values : []).filter(value => typeof value === 'string' && value))]; }
  private _setValues(values: readonly string[]): void { this._dirty = true; this._requested = this._normalize(values); this._sync(); }
  protected _createOption(option: QdsOption): HTMLButtonElement {
    const button = document.createElement('button'); button.type = 'button'; button.dataset.choice = option.value;
    button.textContent = option.label; button.setAttribute('part', 'option'); return button;
  }
  protected _renderOptions(): void { this._control!.replaceChildren(...this._options.map(option => this._createOption(option))); }
  protected _choose(value: string): void {
    if (this.disabled || this.readonly || !this._options.some(option => option.value === value && !option.disabled)) return;
    const old = this.values, next = this.multiple ? old.includes(value) ? old.filter(item => item !== value) : [...old, value] : [value];
    if (JSON.stringify(old) === JSON.stringify(next)) return;
    this._dirty = true; this._requested = next; if (this.validateOn === 'input') this._showError = true; this._sync();
    for (const name of ['input', 'change']) this.dispatchEvent(new CustomEvent(name, {
      bubbles: true, composed: true, detail: { component: this, value: this.value, values: this.values, multiple: this.multiple },
    }));
  }
  private _onClick = (event: Event): void => {
    const button = event.target instanceof HTMLElement ? event.target.closest<HTMLElement>('[data-choice]') : null;
    if (button && this._control!.contains(button)) this._choose(button.dataset.choice!);
  };
  private _onFocusOut = (event: FocusEvent): void => {
    if (event.relatedTarget instanceof Node && this._control!.contains(event.relatedTarget)) return;
    if (this.validateOn === 'blur') this._showError = true; this._sync();
  };
  private _onInvalid = (): void => { this._showError = true; this._sync(); };
  protected _sync(): void {
    if (!this._control) return;
    const focused = this.shadowRoot!.activeElement as HTMLElement | null;
    const restoreFocus = this._optionsChanged && !!focused && this._control.contains(focused);
    const focusedValue = focused?.dataset.choice;
    if (this._optionsChanged) { this._optionsChanged = false; this._renderOptions(); }
    const label = this.shadowRoot!.querySelector<HTMLElement>('[part=label]')!; label.textContent = this.label; label.hidden = !this.label;
    const root = this.getRootNode() as Document | ShadowRoot;
    const labelsRoot = typeof root.getElementById === 'function' ? root : this.ownerDocument;
    const externalName = (this.getAttribute('aria-labelledby') || '').split(/\s+/).map(id => labelsRoot.getElementById(id)?.textContent || '').join(' ').trim();
    const name = this.getAttribute('aria-label') || externalName;
    if (name) { this._control.setAttribute('aria-label', name); this._control.removeAttribute('aria-labelledby'); }
    else { this._control.removeAttribute('aria-label'); if (this.label) this._control.setAttribute('aria-labelledby', label.id); else this._control.removeAttribute('aria-labelledby'); }
    this._syncChoices(); this._validate();
    const visible = (this._showError || this.invalid) && !this.validity.valid;
    this.toggleAttribute('data-invalid', visible); this._control.setAttribute('aria-invalid', String(visible));
    const hint = this.shadowRoot!.querySelector<HTMLElement>('[part=hint]')!, error = this.shadowRoot!.querySelector<HTMLElement>('[part=error]')!;
    hint.textContent = this.hint; hint.hidden = !this.hint || visible; error.textContent = this._message; error.hidden = !visible;
    const described = [!hint.hidden ? hint.id : '', !error.hidden ? error.id : ''].filter(Boolean).join(' ');
    for (const target of [this._control, ...this._buttons()]) { if (described) target.setAttribute('aria-describedby', described); else target.removeAttribute('aria-describedby'); }
    const values = this.values;
    let formValue: string | FormData | null = this.disabled || !values.length ? null : values[0];
    if (this.multiple && formValue !== null) { const data = new FormData(); if (this.name) for (const value of values) data.append(this.name, value); formValue = this.name ? data : null; }
    this._internals?.setFormValue(formValue, JSON.stringify(this._requested));
    if (restoreFocus) (this._buttons().find(button => button.dataset.choice === focusedValue && !button.disabled) || this._focusTarget)?.focus({ preventScroll: true });
  }
  private _validate(): void {
    const valueMissing = this.willValidate && this.required && !this.values.length;
    const customError = this.willValidate && Boolean(this._customError || this.invalid);
    const validity = { valid: !valueMissing && !customError, valueMissing, customError };
    const message = validity.valid ? '' : this._customError || this.errorText || (valueMissing ? 'Choose at least one option.' : 'Please check this choice.');
    const changed = JSON.stringify(validity) !== JSON.stringify(this._validity) || message !== this._message;
    this._validity = validity; this._message = message;
    // WebKit's fallback validation bubble can hide an anchor's author content.
    // A native input isolates that UI and forwards native validation focus.
    this._internals?.setValidity(validity.valid ? {} : { valueMissing, customError }, message, this._validationAnchor);
    if (changed) this.dispatchEvent(new CustomEvent('qds-validity-change', { bubbles: true, composed: true, detail: { component: this, valid: validity.valid, validity: this.validity, message } }));
  }
}
